import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Spin, message } from 'antd';
import { ReloadOutlined, CrownOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useRefreshOwnerSnapshots } from '@/hooks/useOwnerIntelligence';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useAuthStore } from '@/store/authStore';
import { canAccessRouteByPermissions } from '@/config/routePermissions';
import {
  OWNER_INTELLIGENCE_DOMAINS,
  resolveOiNavigation,
  countAlertsForDomain,
} from '@/config/ownerIntelligenceNav';
import {
  OwnerIntelligenceLocaleProvider,
  useOwnerIntelligenceLocale,
} from '@/contexts/OwnerIntelligenceLocaleContext';
import '@/styles/owner-intelligence.css';

const { Title, Text } = Typography;

function OwnerIntelligenceShellInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const refresh = useRefreshOwnerSnapshots();
  const { locale, dir, setLocale } = useOwnerIntelligenceLocale();
  const { domain, subItem } = resolveOiNavigation(location.pathname);
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const canAccessPath = (path: string) => canAccessRouteByPermissions(path, permissions, isSuperAdmin);
  const visibleDomainItems = domain.items.filter((item) => canAccessPath(item.path));
  const activeSubItem = visibleDomainItems.find((item) => item.path === location.pathname) ?? visibleDomainItems[0] ?? subItem;

  const alertsQuery = useQuery({
    queryKey: ['owner-intelligence', 'alerts', 'open'],
    queryFn: () => ownerIntelligenceService.listAlerts('open'),
    staleTime: 60_000,
  });

  const alerts = (alertsQuery.data as Array<{ area?: string; severity?: string; status?: string }>) ?? [];

  const handleRefresh = () => {
    refresh.mutate(undefined, {
      onSuccess: () => {
        message.success('Owner Intelligence data refreshed', 4);
      },
      onError: () => {
        message.error('Refresh failed', 4);
      },
    });
  };

  return (
    <div className="oi-shell" dir={dir}>
      <div className="oi-shell-header">
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <Space className="oi-shell-toolbar" style={{ marginBottom: 8 }}>
              <Link to="/home">
                <Button type="text" icon={<ArrowLeftOutlined />} className="oi-back-link" size="small">
                  Back to ERP
                </Button>
              </Link>
              <div className="oi-locale-toggle" role="group" aria-label="Language">
                <button
                  type="button"
                  className={locale === 'en' ? 'active' : ''}
                  onClick={() => setLocale('en')}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={locale === 'ar' ? 'active' : ''}
                  onClick={() => setLocale('ar')}
                >
                  عر
                </button>
              </div>
              {/* Uses platform ui_language (UserPreferences) — same source as header LanguageSwitcher */}
            </Space>
            <Title level={3} className="oi-page-title">
              <CrownOutlined style={{ marginRight: 8, color: 'var(--oi-signal-blue)' }} />
              Owner Intelligence
            </Title>
            <Text type="secondary">Exception-driven decision intelligence for business owners</Text>
          </div>
          <Button icon={<ReloadOutlined />} loading={refresh.isPending} onClick={handleRefresh}>
            Refresh data
          </Button>
        </Space>
      </div>

      <nav className="oi-domain-nav" aria-label="Owner Intelligence domains">
        {OWNER_INTELLIGENCE_DOMAINS.map((d) => {
          const isActive = d.key === domain.key;
          const badge = countAlertsForDomain(d, alerts);
          const firstVisible = d.items.find((item) => canAccessPath(item.path));
          return (
            <button
              key={d.key}
              type="button"
              className={`oi-domain-nav__item${isActive ? ' oi-domain-nav__item--active' : ''}`}
              onClick={() => firstVisible && navigate(firstVisible.path)}
            >
              {d.label}
              {badge > 0 ? <span className="oi-domain-badge">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      <div className="oi-subnav" role="tablist" aria-label={`${domain.label} views`}>
        {visibleDomainItems.map((item) => (
          <button
            key={item.path}
            type="button"
            role="tab"
            aria-selected={location.pathname === item.path}
            className={`oi-subnav__pill${location.pathname === item.path ? ' oi-subnav__pill--active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="oi-breadcrumb">
        {domain.label} &gt; <strong>{activeSubItem.label}</strong>
      </div>

      <React.Suspense fallback={<Spin style={{ display: 'block', margin: '48px auto' }} />}>
        <Outlet />
      </React.Suspense>
    </div>
  );
}

const OwnerIntelligenceShell: React.FC = () => (
  <OwnerIntelligenceLocaleProvider>
    <OwnerIntelligenceShellInner />
  </OwnerIntelligenceLocaleProvider>
);

export default OwnerIntelligenceShell;
